import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  primary: styles.btnPrimary,
  secondary: styles.btnSecondary,
  ghost: styles.btnGhost,
  danger: styles.btnDanger,
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /**
   * Swaps the leading icon for a spinner and disables the button.
   *
   * Kept as a prop rather than left to callers because a submit button that
   * stays clickable through its own request is how you get two students
   * released on one code.
   */
  loading?: boolean;
  icon?: ReactNode;
  /** Square icon-only button. Requires `aria-label` — there is no text to read.
   *  The glyph may be passed as `icon` or as children; both work. */
  iconOnly?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, icon, iconOnly, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        variants[variant],
        styles.btnSizes[size],
        iconOnly && (size === 'sm' ? 'w-7 px-0' : size === 'lg' ? 'w-9 px-0' : 'w-8 px-0'),
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 size={13} className="animate-spin" aria-hidden />
      ) : (
        /*
          An icon-only button takes its glyph from `icon` OR from its children.

          It used to take it from `icon` only, and DROP children — so
          `<Button iconOnly><Trash2 /></Button>`, which is the spelling anybody
          writes first, rendered a 28×28 button with nothing in it. Clickable,
          reachable by its aria-label, invisible to the eye: the kind of bug
          that survives a typecheck, a test that clicks by label, and a glance
          at the screen, because there is nothing there to look wrong.

          Both spellings now mean the same thing.
        */
        icon ?? (iconOnly ? children : null)
      )}
      {!iconOnly && children}
    </button>
  );
});
